import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

/**
 * Underline-style tab bar, horizontally scrollable.
 * tabs: [{ key, label, count, icon }] — controlled via active/onChange.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE ARE ARROWS
 * ---------------------------------------------------------------------------
 * This bar has always scrolled. It was `overflow-x-auto scrollbar-hide`, and
 * `scrollbar-hide` removed the ONLY thing telling anyone there was more to see.
 * On a screen like Reports — twelve tabs — the ones past the edge simply did not
 * exist as far as the operator was concerned: nothing to see, nothing to click,
 * and no reason to try dragging a bar that looked complete.
 *
 * So the scrollbar stays hidden (it is ugly across the four screens this renders
 * on) and it is replaced by something better: a chevron at each edge, shown ONLY
 * when there is actually more in that direction. An edge with nothing past it
 * has no chevron, so the affordance doubles as the answer to "am I at the end?".
 *
 * ---------------------------------------------------------------------------
 * WHY DIRECTION IS READ FROM THE DOM
 * ---------------------------------------------------------------------------
 * This app is Arabic-first, so the bar is usually RTL, where "the end of the
 * list" is to the LEFT and scrollLeft runs NEGATIVE (0 at the start, down to
 * -(scrollWidth - clientWidth) at the end).
 *
 * Direction comes from `getComputedStyle(el).direction`, NOT from i18n. Two
 * reasons, and the second is a trap this repo has already been bitten by:
 *   1. The element knows its own direction. A `dir` set by any ancestor, or a
 *      single component deliberately overriding it, is respected for free.
 *   2. `i18n.language` is not reliably 'ar' — it can be 'ar-SA', and a strict
 *      compare against 'ar' is FALSE while t() still resolves Arabic. Anything
 *      keying layout off that comparison silently renders LTR for those users.
 *
 * Every scrollLeft comparison below is written through Math.abs() so it holds
 * whichever sign the browser reports, rather than assuming the modern spec.
 */
export default function Tabs({ tabs = [], active, onChange, className = '' }) {
  const scrollerRef = useRef(null);
  const activeRef = useRef(null);
  // Only a CHANGE of tab scrolls: scrollIntoView walks every scrollable
  // ancestor, so firing it on mount yanks the whole page down to a tab bar that
  // was already where the user expected it.
  //
  // This tracks the PREVIOUS active key rather than a "have I mounted yet"
  // boolean. A boolean survives StrictMode's deliberate double-invoke of mount
  // effects, so the first pass set it and the second pass sailed past the guard
  // and scrolled — on mount, in dev, on every screen that renders this.
  // Production never double-invokes so it never shipped, but comparing the
  // actual value is both correct under the double-invoke and a truer statement
  // of the rule: scroll when the tab changed.
  const prevActiveRef = useRef(active);

  // `left`/`right` are PHYSICAL sides, already resolved for direction. Keeping
  // the state physical is deliberate: the two things a chevron does — which
  // edge it sits on and which way it scrolls — are both physical, so resolving
  // logical→physical once here means nothing downstream has to think about RTL
  // again. An earlier version kept these logical and flipped the scroll sign
  // instead, which is wrong: the left chevron scrolls left in BOTH directions.
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    // 1px of slack: sub-pixel layout means scrollLeft rarely lands exactly on
    // the bound, and without it a chevron flickers at a hard edge forever.
    const max = el.scrollWidth - el.clientWidth;
    // Math.abs because RTL runs scrollLeft NEGATIVE — 0 at the start (the right
    // edge) down to -(scrollWidth - clientWidth) at the end. Compared raw, a
    // mid-scrolled RTL bar reads as sitting at its start and loses an arrow.
    const offset = Math.abs(el.scrollLeft);

    const atStart = offset <= 1; // start = the READING start: left in LTR, right in RTL
    const atEnd = max - offset <= 1;
    const rtl = getComputedStyle(el).direction === 'rtl';

    const left = rtl ? !atEnd : !atStart;
    const right = rtl ? !atStart : !atEnd;

    // Bail when nothing moved. measure() runs on every scroll event, and a
    // smooth scroll emits one per frame while only ever changing these two
    // booleans twice — at the moment an edge is reached or left. Publishing a
    // fresh object regardless re-rendered the whole bar ~30 times per nudge for
    // no visible difference.
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;

    measure();

    // Three things change whether a chevron belongs on screen, and the tab set
    // is only one of them: the container resizes (window, sidebar collapse,
    // device rotation) and the content resizes (a count badge appearing widens
    // a tab). A ResizeObserver on both covers all three; a window listener
    // alone misses the second.
    //
    // Guarded because jsdom has no ResizeObserver, and twelve screens render
    // this component — unguarded, `new ResizeObserver` throws at mount and
    // takes every one of their existing suites down with it. The window-resize
    // fallback covers the container case, which is the one that matters
    // outside a test.
    const hasRO = typeof ResizeObserver !== 'undefined';
    const ro = hasRO ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    } else {
      window.addEventListener('resize', measure);
    }
    el.addEventListener('scroll', measure, { passive: true });

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', measure);
      el.removeEventListener('scroll', measure);
    };
  }, [measure, tabs.length]);

  useEffect(() => {
    if (prevActiveRef.current === active) return;
    prevActiveRef.current = active;
    // `inline: 'nearest'` is the logical axis, so it already means "the reading
    // end" in RTL — no sign arithmetic here. 'nearest' also does nothing when
    // the tab is fully visible, which is what makes selecting a middle tab feel
    // still rather than jumpy. `block: 'nearest'` keeps it from scrolling the
    // page vertically on the way.
    //
    // Feature-tested, not assumed: jsdom implements neither scrollIntoView nor
    // scrollBy, and twelve screens render this component. Called blind, this
    // throws inside a commit effect and takes down every one of their suites —
    // which is exactly what happened, and was invisible from this component's
    // own tests because those stub the method globally.
    const el = activeRef.current;
    if (typeof el?.scrollIntoView !== 'function') return;
    el.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [active]);

  /**
   * Scroll roughly one "page" of tabs, capped so a very wide bar does not skip
   * its own middle.
   *
   * No direction logic. `side` is physical and so is scrollBy's axis: the left
   * chevron reveals what is to the left, which is a NEGATIVE delta, in LTR and
   * RTL alike. Direction was already accounted for in measure(), when deciding
   * which side gets a chevron at all.
   */
  const nudge = (side) => {
    const el = scrollerRef.current;
    // Same feature test as the effect above, same reason.
    if (typeof el?.scrollBy !== 'function') return;
    const step = Math.max(120, Math.min(el.clientWidth * 0.75, 360));
    el.scrollBy({ left: side === 'left' ? -step : step, behavior: 'smooth' });
  };

  /**
   * Fade the tabs out under each chevron.
   *
   * The chevron used to paint itself `bg-surface` to cover the tab sliding
   * beneath it. That colour is only right inside a Card or a Modal — on the
   * eight of twelve screens that render this bar straight onto `bg-canvas` it
   * was a visibly wrong opaque box, hard-cropping the tab at its edge. Picking
   * the other token just inverts which screens are wrong.
   *
   * So nothing is painted at all: the SCROLLER is masked, so the tab itself
   * dissolves toward the edge and the chevron sits on whatever background the
   * consumer actually has. Correct on all twelve for free, and a dissolve reads
   * as "there is more this way" better than a box ever did.
   *
   * `to right` is physical, exactly like the chevrons and the scroll deltas —
   * CSS gradients are not flipped by `direction`, so a side that has a chevron
   * has its fade in RTL and LTR alike, with no sign arithmetic. An edge with no
   * chevron gets an opaque stop, i.e. no fade, so a bar that fits is untouched.
   */
  const fade = `linear-gradient(to right, ${
    edges.left ? 'transparent 0, #000 2.5rem' : '#000 0'
  }, ${edges.right ? '#000 calc(100% - 2.5rem), transparent 100%' : '#000 100%'})`;

  return (
    <div className={`relative border-b border-edge ${className}`}>
      <Arrow side="left" show={edges.left} onNudge={nudge} />
      <Arrow side="right" show={edges.right} onNudge={nudge} />

      <div
        ref={scrollerRef}
        className="overflow-x-auto scrollbar-hide"
        style={{ WebkitMaskImage: fade, maskImage: fade }}
      >
        <nav className="flex gap-1 min-w-max" role="tablist">
          {tabs.map(({ key, label, count, icon: Icon }) => {
            const isActive = key === active;
            return (
              <button
                key={key}
                ref={isActive ? activeRef : null}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange?.(key)}
                className={`inline-flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                  isActive
                    ? 'border-accent text-accent'
                    : 'border-transparent text-content-secondary hover:text-content hover:border-edge-strong'
                }`}
              >
                {Icon && <Icon size={16} />}
                {label}
                {count !== undefined && (
                  <span
                    className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                      isActive ? 'bg-accent-soft text-accent-hover dark:text-accent' : 'bg-surface-2 text-content-secondary'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

/**
 * An edge chevron. Module scope, NOT declared inside the render body, and that
 * placement is load-bearing rather than tidiness: a component declared inline is
 * a brand-new type on every render, so React unmounts and remounts the button
 * DOM node underneath it. Clicking a chevron starts a 500ms smooth scroll that
 * emits a scroll event per frame; with an inline type every one of those frames
 * replaced the button, and a second click whose mousedown landed on one node and
 * mouseup on its replacement produced NO click event at all. Paging through the
 * twelve-tab Reports bar dropped clicks at random. This repo has shipped this
 * exact defect class before, in ResponsiveTable.
 */
function Arrow({ side, show, onNudge }) {
  if (!show) return null;
  const Icon = side === 'left' ? FiChevronLeft : FiChevronRight;
  return (
    <button
      type="button"
      // Redundant for keyboard and screen-reader users: they reach every tab
      // through the tablist itself, and the effect's scrollIntoView brings it
      // into view. Exposing these would add two junk stops per tab bar.
      aria-hidden="true"
      tabIndex={-1}
      onClick={() => onNudge(side)}
      className={`absolute top-0 bottom-px z-10 flex items-center px-1 ${
        side === 'left' ? 'left-0' : 'right-0'
      } text-content-secondary hover:text-content`}
    >
      <Icon size={18} />
    </button>
  );
}
